/**
 * Maps backend Kamino-sync error codes to pt-BR user messages + recovery hints.
 *
 * `KAMINO_COA_SYNC_ENTITY_REQUIRED` mirrors the code thrown from
 * `apps/api/src/modules/integration/kamino/kamino-sync-config.service.ts`'s
 * `resolveSyncEntityId` (ALC-2004): the `add_to_active` chart-of-accounts sync mode no
 * longer picks an arbitrary tenant-wide "active" version — it requires an entity, either
 * on the request or previously saved on the Kamino sync configuration.
 */

export interface KaminoSyncErrorCopy {
  userMessage: string;
  recoveryHint: string;
}

const TEMPLATES: Record<string, KaminoSyncErrorCopy> = {
  KAMINO_COA_SYNC_ENTITY_REQUIRED: {
    userMessage:
      'Não é possível sincronizar o plano de contas do Kamino: nenhuma entidade foi informada.',
    recoveryHint:
      'Selecione a entidade que deve receber as contas importadas (ou salve uma entidade padrão na configuração de sincronização do Kamino) e tente novamente.',
  },
};

/** Client-side fallback copy for a Kamino-sync error code. */
export function getKaminoSyncErrorCopy(code: string): KaminoSyncErrorCopy | null {
  return TEMPLATES[code] ?? null;
}

/** Extract a structured Kamino-sync error payload ({ code, message }) from an API error. */
export function extractKaminoSyncErrorPayload(
  err: unknown,
): { code: string; message?: string } | null {
  const maybeError = err as { response?: { data?: unknown }; details?: unknown };
  const data = (maybeError?.response?.data ?? maybeError?.details) as
    | { code?: string; message?: string | { code?: string; message?: string }; details?: unknown }
    | undefined;
  if (!data) return null;

  if (typeof data.message === 'object' && data.message) {
    const inner = data.message;
    if (inner.code && inner.code in TEMPLATES) {
      return { code: inner.code, message: inner.message };
    }
  }
  if (data.code && data.code in TEMPLATES) {
    return {
      code: data.code,
      message: typeof data.message === 'string' ? data.message : undefined,
    };
  }
  if (data.details) {
    return extractKaminoSyncErrorPayload({ details: data.details });
  }
  return null;
}
